import { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';

interface PriceRequest {
  id: string;
  userId: string;
  userName: string;
  companyName: string;
  productName?: string;
  baselinker_id?: string;
  marketplace?: string;
  currentPrice?: number;
  newPrice?: number;
  message: string;
  status: 'pending' | 'completed';
  createdAt: string;
  completedAt?: string;
  completionNote?: string;
  type: 'price' | 'domain';
  customDomain?: string;
}

interface RequestDetailsModalProps {
  request: PriceRequest;
  onClose: () => void;
  onComplete: (requestId: string, completionNote: string) => Promise<void>;
}

export default function RequestDetailsModal({
  request,
  onClose,
  onComplete
}: RequestDetailsModalProps) {
  const [loading, setLoading] = useState(false);
  const [completionNote, setCompletionNote] = useState('');
  const [error, setError] = useState('');

  const handleComplete = async () => {
    if (!completionNote.trim()) {
      setError('Por favor, adicione uma nota de conclusão');
      return;
    }

    setLoading(true);
    try {
      await onComplete(request.id, completionNote.trim());
    } catch (err) {
      console.error('Error completing request:', err);
      setError('Erro ao concluir pedido');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">Detalhes do Pedido</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Request Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Solicitante</p>
              <p className="font-medium">{request.userName}</p>
              <p className="text-sm text-gray-600">{request.companyName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Data do Pedido</p>
              <p className="font-medium">{formatDate(request.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Produto</p>
              <p className="font-medium">{request.productName}</p>
              <p className="text-sm text-gray-600">ID BaseLinker: {request.baselinker_id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Marketplace</p>
              <p className="font-medium">{request.marketplace}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Preço Atual</p>
              <p className="font-medium">€{request.currentPrice?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Novo Preço</p>
              <p className="font-medium">€{request.newPrice?.toFixed(2)}</p>
            </div>
          </div>

          {/* Message */}
          {request.message && (
            <div>
              <p className="text-sm text-gray-500 mb-2">Mensagem do Cliente</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="whitespace-pre-wrap">{request.message}</p>
              </div>
            </div>
          )}

          {/* Completion Note */}
          {request.status === 'completed' ? (
            <div>
              <p className="text-sm text-gray-500 mb-2">Nota de Conclusão</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>{request.completionNote}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Concluído em: {request.completedAt && formatDate(request.completedAt)}
                </p>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nota de Conclusão
              </label>
              <textarea
                value={completionNote}
                onChange={(e) => setCompletionNote(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                rows={4}
                placeholder="Adicione uma nota sobre a conclusão do pedido..."
              />
              {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Fechar
            </button>
            {request.status === 'pending' && (
              <button
                onClick={handleComplete}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                <CheckCircle size={20} />
                {loading ? 'Processando...' : 'Marcar como Efetuado'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}